import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  UserPlus,
  MoreHorizontal,
  Edit2,
  Eye,
  Shield,
  Loader2,
  Crown,
  UserMinus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { UserAvatar } from '@/components/ui/user-avatar';
import { Switch } from '@/components/ui/switch';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { cn } from '@/lib/utils';
import { useTeam, type TeamMember } from '@/hooks/useTeam';
import { useLocations } from '@/hooks/useLocations';
import { useAuth } from '@/hooks/useAuth';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { CreateStaffDialog } from '@/components/team/CreateStaffDialog';
import { EditStaffDialog } from '@/components/team/EditStaffDialog';
import { toast } from 'sonner';

const ROLE_COLORS: Record<string, string> = {
  superadmin: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  primary_admin: 'bg-rose-500/10 text-rose-600 border-rose-500/20',
  owner: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  admin: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  manager: 'bg-green-500/10 text-green-600 border-green-500/20',
  employee: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  kiosk: 'bg-cyan-500/10 text-cyan-600 border-cyan-500/20',
};

const ROLE_LABELS: Record<string, string> = {
  superadmin: 'Super Admin',
  primary_admin: 'Business Owner',
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  employee: 'Cashier',
};

// Editable currency input for targets
function TargetInput({
  value,
  onSave,
  disabled,
}: {
  value: number;
  onSave: (value: number) => void;
  disabled?: boolean;
}) {
  const [inputValue, setInputValue] = useState(value.toFixed(2));

  const handleBlur = () => {
    const numValue = parseFloat(inputValue) || 0;
    if (numValue !== value) {
      onSave(numValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div className="relative">
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
      <Input
        type="number"
        step="0.01"
        min="0"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className="w-24 pl-5 h-8 text-sm"
      />
    </div>
  );
}

// Relative time formatter
function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
  if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
  if (diffYears === 1) return 'last year';
  return `${diffYears} years ago`;
}

interface TeamManagementProps {
  showHeader?: boolean;
}

export function TeamManagement({ showHeader = true }: TeamManagementProps) {
  const navigate = useNavigate();
  const { teamMembers, isLoading, updateSalesTargets, toggleStaffActive, refetch } = useTeam();
  const { locations } = useLocations();
  const { profile, isSuperadmin, isAdmin } = useAuth();
  const { startImpersonation, canImpersonate } = useImpersonation();
  const {
    designatePrimaryAdmin,
    revokePrimaryAdmin,
  } = useTeam();
  const { flags } = useFeatureFlags();

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [outletFilter, setOutletFilter] = useState<string>('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<TeamMember | null>(null);
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const [togglingActive, setTogglingActive] = useState<string | null>(null);

  // Primary admin designation state
  const [designatingPrimaryAdmin, setDesignatingPrimaryAdmin] = useState<string | null>(null);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    title: string;
    description: string;
    confirmText: string;
    variant: 'default' | 'destructive';
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    description: '',
    confirmText: 'Confirm',
    variant: 'default',
    onConfirm: () => {},
  });

  const filteredMembers = teamMembers.filter(member => {
    // Role filter
    if (roleFilter !== 'all' && member.role !== roleFilter) return false;

    // Outlet filter
    if (outletFilter !== 'all') {
      const hasAccess = member.location_access?.some(la => la.location_id === outletFilter);
      if (!hasAccess) return false;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const fullName = `${member.first_name || ''} ${member.last_name || ''}`.toLowerCase();
      const email = (member.email || '').toLowerCase();
      const username = (member.username || '').toLowerCase();
      return fullName.includes(query) || email.includes(query) || username.includes(query);
    }

    return true;
  });

  const handleEditClick = (member: TeamMember) => {
    setSelectedStaff(member);
    setEditDialogOpen(true);
  };

  const handleImpersonate = async (member: TeamMember) => {
    setImpersonating(member.id);
    try {
      const canDo = await canImpersonate(member.id);
      if (!canDo) {
        alert('You do not have permission to impersonate this user');
        return;
      }
      await startImpersonation(member.id, 'Admin review');

      // Redirect to a page the impersonated user can access
      navigate('/sell/register');
    } catch (error: any) {
      alert(error.message || 'Failed to start impersonation');
    } finally {
      setImpersonating(null);
    }
  };

  const handleToggleActive = async (member: TeamMember, isActive: boolean) => {
    setTogglingActive(member.id);
    try {
      await toggleStaffActive.mutateAsync({ id: member.id, isActive });
    } catch (error: any) {
      alert(error.message || 'Failed to update status');
    } finally {
      setTogglingActive(null);
    }
  };

  const handleUpdateTarget = async (
    memberId: string,
    field: 'daily_target' | 'weekly_target' | 'monthly_target',
    value: number
  ) => {
    try {
      await updateSalesTargets.mutateAsync({
        id: memberId,
        targets: { [field]: value },
      });
    } catch (error: any) {
      console.error('Failed to update target:', error);
    }
  };

  // Note: getInitials is no longer needed as UserAvatar handles initials internally

  // Handle designating primary admin
  const handleDesignatePrimaryAdmin = (member: TeamMember) => {
    if (!profile?.organization_id) return;

    setConfirmModal({
      open: true,
      title: 'Designate Business Owner',
      description: `Are you sure you want to designate ${member.first_name} ${member.last_name} as the Business Owner? This will give them full access to all business features.`,
      confirmText: 'Designate',
      variant: 'default',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }));
        setDesignatingPrimaryAdmin(member.id);
        try {
          await designatePrimaryAdmin.mutateAsync({
            userId: member.id,
            organizationId: profile.organization_id!,
          });
          toast.success('Business Owner Designated', {
            description: `${member.first_name} ${member.last_name} is now the Business Owner`,
          });
          refetch();
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Failed to designate business owner';
          toast.error('Error', {
            description: message,
          });
        } finally {
          setDesignatingPrimaryAdmin(null);
        }
      },
    });
  };

  // Handle revoking primary admin
  const handleRevokePrimaryAdmin = (member: TeamMember) => {
    setConfirmModal({
      open: true,
      title: 'Revoke Business Owner',
      description: `Are you sure you want to revoke Business Owner status from ${member.first_name} ${member.last_name}? They will be demoted to Admin.`,
      confirmText: 'Revoke',
      variant: 'destructive',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }));
        setDesignatingPrimaryAdmin(member.id);
        try {
          await revokePrimaryAdmin.mutateAsync(member.id);
          toast.success('Business Owner Revoked', {
            description: `${member.first_name} ${member.last_name} has been demoted to Admin`,
          });
          refetch();
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Failed to revoke business owner';
          toast.error('Error', {
            description: message,
          });
        } finally {
          setDesignatingPrimaryAdmin(null);
        }
      },
    });
  };

  const getOutletDisplay = (member: TeamMember) => {
    if (!member.location_access || member.location_access.length === 0) {
      return 'No locations';
    }
    if (member.location_access.length === locations.length) {
      return 'All locations';
    }
    if (member.location_access.length === 1) {
      return member.location_access[0].location_name || 'Unknown';
    }
    return `${member.location_access.length} locations`;
  };

  const canManageStaff = isSuperadmin || isAdmin || profile?.role === 'owner';

  return (
    <>
      {/* Header */}
      {showHeader && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold">User Management</h2>
            <p className="text-sm text-muted-foreground">
              Manage users and their sales targets
            </p>
          </div>
          {canManageStaff && (
            <Button variant="blaze" className="gap-2" onClick={() => setCreateDialogOpen(true)}>
              <UserPlus className="h-4 w-4" />
              Add user
            </Button>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div className="flex-1 min-w-[200px] max-w-sm">
          <label className="text-sm font-medium mb-1.5 block">Search for users</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Enter a name or email address"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="min-w-[150px]">
          <label className="text-sm font-medium mb-1.5 block">Role</label>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger>
              <SelectValue placeholder="All roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {isSuperadmin && <SelectItem value="primary_admin">Business Owner</SelectItem>}
              <SelectItem value="owner">Owner</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="employee">Cashier</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-[150px]">
          <label className="text-sm font-medium mb-1.5 block">Outlet</label>
          <Select value={outletFilter} onValueChange={setOutletFilter}>
            <SelectTrigger>
              <SelectValue placeholder="All locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {locations.map((location) => (
                <SelectItem key={location.id} value={location.id}>
                  {location.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button variant="default">Search</Button>
      </div>

      {/* Users Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading users...</span>
          </div>
        </div>
      ) : filteredMembers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <Shield className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-medium">No users found</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            {searchQuery ? 'Try a different search term' : 'Start by adding your first user'}
          </p>
          {canManageStaff && (
            <Button variant="blaze" className="gap-2" onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              Add user
            </Button>
          )}
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                    User
                  </th>
                  <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                    Role
                  </th>
                  <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                    Outlet
                  </th>
                  <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                    Daily target
                  </th>
                  <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                    Weekly target
                  </th>
                  <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                    Monthly target
                  </th>
                  <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                    Last active
                  </th>
                  <th className="text-center text-sm font-medium text-muted-foreground px-4 py-3">
                    Enabled
                  </th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredMembers.map((member) => (
                  <tr
                    key={member.id}
                    className={cn(
                      'hover:bg-muted/30 transition-colors',
                      !member.is_active && 'opacity-60'
                    )}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <UserAvatar
                          userId={member.id}
                          firstName={member.first_name}
                          lastName={member.last_name}
                          role={member.role}
                          size="md"
                          useVibrantColors={flags.vibrantAvatars}
                        />
                        <div className="min-w-0">
                          <button
                            onClick={() => handleEditClick(member)}
                            className="font-medium text-sm hover:underline text-left block truncate"
                          >
                            {member.first_name} {member.last_name}
                            {member.id === profile?.id && (
                              <span className="text-xs text-muted-foreground ml-1">(You)</span>
                            )}
                          </button>
                          <p className="text-xs text-muted-foreground truncate">
                            {member.email || (member.username ? `@${member.username}` : 'No email')}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant="outline"
                        className={cn('capitalize', ROLE_COLORS[member.role])}
                      >
                        {ROLE_LABELS[member.role] || member.role}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {getOutletDisplay(member)}
                    </td>
                    <td className="px-4 py-3">
                      <TargetInput
                        value={member.daily_target}
                        onSave={(value) => handleUpdateTarget(member.id, 'daily_target', value)}
                        disabled={!canManageStaff || member.id === profile?.id}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <TargetInput
                        value={member.weekly_target}
                        onSave={(value) => handleUpdateTarget(member.id, 'weekly_target', value)}
                        disabled={!canManageStaff || member.id === profile?.id}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <TargetInput
                        value={member.monthly_target}
                        onSave={(value) => handleUpdateTarget(member.id, 'monthly_target', value)}
                        disabled={!canManageStaff || member.id === profile?.id}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatRelativeTime(member.last_active_at)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Switch
                        checked={member.is_active}
                        onCheckedChange={(checked) => handleToggleActive(member, checked)}
                        disabled={
                          !canManageStaff ||
                          member.id === profile?.id ||
                          togglingActive === member.id
                        }
                      />
                    </td>
                    <td className="px-2 py-3">
                      {canManageStaff && member.id !== profile?.id && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditClick(member)}>
                              <Edit2 className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {(isSuperadmin || isAdmin) && (
                              <DropdownMenuItem
                                onClick={() => handleImpersonate(member)}
                                disabled={impersonating === member.id}
                              >
                                {impersonating === member.id ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <Eye className="h-4 w-4 mr-2" />
                                )}
                                Impersonate
                              </DropdownMenuItem>
                            )}
                            {/* Superadmin-only: Designate/Revoke Business Owner */}
                            {isSuperadmin && member.role !== 'superadmin' && member.role !== 'primary_admin' && (
                              <DropdownMenuItem
                                onClick={() => handleDesignatePrimaryAdmin(member)}
                                disabled={designatingPrimaryAdmin === member.id}
                              >
                                {designatingPrimaryAdmin === member.id ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <Crown className="h-4 w-4 mr-2" />
                                )}
                                Designate as Business Owner
                              </DropdownMenuItem>
                            )}
                            {isSuperadmin && member.role === 'primary_admin' && (
                              <DropdownMenuItem
                                onClick={() => handleRevokePrimaryAdmin(member)}
                                disabled={designatingPrimaryAdmin === member.id}
                                className="text-amber-600"
                              >
                                {designatingPrimaryAdmin === member.id ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <UserMinus className="h-4 w-4 mr-2" />
                                )}
                                Revoke Business Owner
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dialogs */}
      <CreateStaffDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={refetch}
      />

      <EditStaffDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        staff={selectedStaff}
        onSuccess={refetch}
      />

      {/* Confirmation Modal */}
      <ResponsiveModal
        open={confirmModal.open}
        onOpenChange={(value) => setConfirmModal(prev => ({ ...prev, open: value }))}
      >
        <ResponsiveModalContent maxWidth="sm">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>{confirmModal.title}</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {confirmModal.description}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmModal(prev => ({ ...prev, open: false }))}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant={confirmModal.variant === 'destructive' ? 'destructive' : 'blaze'}
              onClick={confirmModal.onConfirm}
            >
              {confirmModal.confirmText}
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </>
  );
}
